import './styles.scss'

import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Collapse, Drawer } from 'antd'

import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/components/Icons'
import { useAppSelector } from '@/store/hook'
import {
  getIsLoadingSubmenuProductCategoryList,
  selectSubmenuProductCategories,
} from '@/store/selectors/categorySelector'
import {
  selectEquipmentManagerLink,
  selectShouldRenderServiceMenu,
} from '@/store/selectors/configSelector'
import usePermission from '@/hooks/usePermission'
import { useSearchContext } from '@/contexts/search/useSearchContext'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'

import SidebarMenu from '../../../SidebarMenu'
import HeaderLogoSection from '../../components'
import ServiceSubmenuMobile from '../../ServicesMenu/Mobile'
import HeaderSubmenuMobile from '../../Submenu/Mobile'

const { Panel } = Collapse

interface HeaderMenuMobileProps {
  isShow: boolean
  onClose: () => void
  onOpen: () => void
}

function HeaderMenuMobile({ isShow, onClose, onOpen }: HeaderMenuMobileProps): React.ReactElement {
  const { t } = useTranslation()
  const { setIsSearchFocused } = useSearchContext()
  const [expandMenuList, setExpandMenuList] = useState(false)

  const isLoadingCategoryList = useAppSelector(
    getIsLoadingSubmenuProductCategoryList,
  )
  const productCategories = useAppSelector(selectSubmenuProductCategories)
  const equipmentManagerLink = useAppSelector(selectEquipmentManagerLink)
  const shouldRenderServiceMenu = useAppSelector(selectShouldRenderServiceMenu)
  const { hasPermission: canViewCategoriesList } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.USE_PRODUCT_NAVIGATION,
  )

  const onChangeCollapseExpandIcon = (key: string | string[]) => {
    // key length !== 0 -> collapse is expand
    setExpandMenuList(key.length > 0)
  }

  const onFocusSearch = useCallback(() => {
    setIsSearchFocused(true)
  }, [setIsSearchFocused])

  const renderExpandIcon = () => expandMenuList ? <ChevronUpIcon /> : <ChevronDownIcon />

  return (
    <Drawer
      closable
      closeIcon={<CloseIcon />}
      open={isShow}
      placement="right"
      rootClassName="header-menu-drawer"
      width={window.innerWidth}
      title={
        <HeaderLogoSection
          inBurgerMenu
          onHideDrawerMobile={onClose}
          onShowDrawerMobile={onOpen}
        />
      }
      onClose={onClose}
    >
      {/* products submenu */}
      {canViewCategoriesList && (
        <HeaderSubmenuMobile
          btnName={t('product')}
          isLoading={isLoadingCategoryList}
          isShow={isShow}
          productCategories={productCategories}
          onClose={onClose}
          onFocusSearch={onFocusSearch}
        />
      )}
      {/* services submenu */}
      {shouldRenderServiceMenu && (
        <ServiceSubmenuMobile
          btnName={t('services')}
          isShow={isShow}
          onClose={onClose}
          onFocusSearch={onFocusSearch}
        />
      )}
      {!!equipmentManagerLink && (
        <div className="submenu-mobile">
          <a
            className="submenu-mobile__btn"
            href={equipmentManagerLink}
            rel="noopener noreferrer"
            target="_blank"
            type="button"
          >
            {t('equipmentManagement')}
          </a>
        </div>
      )}
      {/* workspace menu */}
      <Collapse
        className="sidebar__collapse--wrapper"
        expandIcon={() => renderExpandIcon()}
        expandIconPosition="end"
        onChange={onChangeCollapseExpandIcon}
      >
        <Panel
          key="1"
          className="sidebar__header"
          header={t('myBusinessWorkspace')}
        >
          <SidebarMenu />
        </Panel>
      </Collapse>
    </Drawer>
  )
}

export default HeaderMenuMobile
